import i18n, { defaultLanguage } from '../i18n/config';
import { env, joinApiUrl } from '../config/env';

import { refreshAccessTokenSingleFlight } from './tokenRefresh';

function currentAcceptLanguage(): string {
  return i18n.resolvedLanguage || i18n.language || defaultLanguage;
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

// FastAPI returns `detail` as a plain string for HTTPExceptions, but as an
// array of `{ loc, msg, ... }` objects for 422 validation errors. Coercing the
// array straight into `new Error()` yields "[object Object],..." in the UI, so
// flatten it into a readable, field-qualified message instead.
function extractApiErrorMessage(payload: unknown): string {
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const parts = detail
        .map((item) => {
          if (item && typeof item === 'object') {
            const entry = item as { loc?: unknown[]; msg?: unknown };
            const field = Array.isArray(entry.loc)
              ? entry.loc.filter((part) => part !== 'body').join('.')
              : '';
            const msg = typeof entry.msg === 'string' ? entry.msg : '';
            return field && msg ? `${field}: ${msg}` : msg;
          }
          return typeof item === 'string' ? item : '';
        })
        .filter(Boolean);
      if (parts.length > 0) return parts.join('; ');
    }
  }
  return 'Unexpected API error';
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  token?: string | null;
};

async function apiRequestOnce<T>(
  path: string,
  options: RequestOptions,
  accessToken: string | null | undefined,
): Promise<Response> {
  const isFormData = options.body instanceof FormData;
  const requestBody =
    options.body == null
      ? undefined
      : isFormData
        ? (options.body as BodyInit)
        : (JSON.stringify(options.body) as BodyInit);
  return fetch(resolveApiUrl(path), {
    method: options.method ?? 'GET',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'Accept-Language': currentAcceptLanguage(),
    },
    body: requestBody,
  });
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let accessToken = options.token ?? null;
  let response = await apiRequestOnce<T>(path, options, accessToken);

  if (
    response.status === 401 &&
    accessToken &&
    path !== '/auth/refresh' &&
    path !== '/auth/login' &&
    path !== '/auth/logout'
  ) {
    const next = await refreshAccessTokenSingleFlight();
    if (next) {
      accessToken = next;
      response = await apiRequestOnce<T>(path, options, accessToken);
    }
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as unknown;
    throw new ApiError(extractApiErrorMessage(payload), response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export type AuthenticatedFetchOptions = {
  /**
   * Aborts the request. Large downloads (a 19 MB mesh) keep the connection and the
   * response body alive long after the caller has moved on, so anything that can be
   * navigated away from should pass one.
   */
  readonly signal?: AbortSignal;
};

export async function fetchAuthenticatedResponse(
  path: string,
  token: string,
  options: AuthenticatedFetchOptions = {},
): Promise<Response> {
  const { signal } = options;
  let accessToken = token;
  let response = await fetch(resolveApiUrl(path), {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Accept-Language': currentAcceptLanguage(),
    },
    signal,
  });

  if (response.status === 401) {
    const next = await refreshAccessTokenSingleFlight();
    if (next) {
      accessToken = next;
      response = await fetch(resolveApiUrl(path), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept-Language': currentAcceptLanguage(),
        },
        signal,
      });
    }
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as unknown;
    throw new ApiError(extractApiErrorMessage(payload), response.status);
  }
  return response;
}

export async function fetchAuthenticatedBlob(
  path: string,
  token: string,
  options: AuthenticatedFetchOptions = {},
): Promise<Blob> {
  const response = await fetchAuthenticatedResponse(path, token, options);
  return response.blob();
}

export function resolveApiUrl(path: string): string {
  return joinApiUrl(env.apiBaseUrl, path);
}

export function buildWebSocketUrl(
  baseUrl: string,
  path: string,
  accessToken: string,
  queryParams: Record<string, string | null | undefined> = {},
): string {
  const url = new URL(joinApiUrl(baseUrl, path));
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('access_token', accessToken);
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
}
